import {
 collection,
 addDoc,
 updateDoc,
 doc,
 getDoc,
 deleteDoc,
 arrayUnion,
 arrayRemove,
 serverTimestamp
} from "firebase/firestore";
import { databasePromise } from "configuration";
import { sanitizeText, getDeviceId } from "utilities";
import { rateLimitService } from "./rateLimit";
import { getValidFirebaseToken } from "./authentication";
import { v4 as uuidv4 } from "uuid";

const decrementAggregates = async (review, database) => {
 const isDeal = !!review.dealId;
 const parentId = isDeal ? review.dealId : review.restaurantId;
 if (!parentId) return;

 const collectionName = isDeal ? "deals" : "restaurants";
 const parentRef = doc(database, collectionName, parentId);
 const parentSnap = await getDoc(parentRef);
 if (!parentSnap.exists()) return;

 const { reviewCount = 0, rating = 0 } = parentSnap.data();
 const newCount = Math.max(0, reviewCount - 1);
 const currentSum = (rating || 0) * reviewCount;
 const newSum = Math.max(0, currentSum - (review.rating || 0));
 const newRating = newCount > 0 ? parseFloat((newSum / newCount).toFixed(1)) : null;

 await updateDoc(parentRef, { reviewCount: newCount, rating: newRating });
};

/**
 * Strip undefined values from an object so Firestore doesn't reject the write.
 * Firestore doesn't accept undefined — use null for intentionally absent fields.
 */
const stripUndefined = (obj) =>
 Object.fromEntries(
  Object.entries(obj).filter(([, v]) => v !== undefined),
 );

export const reviewService = {
 async createReview(reviewData) {
  try {
   const database = await databasePromise;

   const firebaseUser = await getValidFirebaseToken();
   if (!firebaseUser) {
    throw new Error('Authentication required. Please sign in and try again.');
   }

   const sanitizedData = {
    ...reviewData,
    // Only include dealTitle if it was provided — omit entirely if absent
    // to avoid Firestore rejecting an undefined field value
    ...(reviewData.dealTitle != null && {
     dealTitle: sanitizeText(reviewData.dealTitle, 100),
    }),
    description: sanitizeText(reviewData.description || "", 500),
   };

   // Strip any remaining undefined values before writing
   const cleanData = stripUndefined(sanitizedData);

   const docRef = await addDoc(collection(database, "reviews"), {
    ...cleanData,
    status: "pendingCreate",
    createdDate: new Date().toISOString(),
    likedBy: [],
    dislikedBy: [],
    reportedBy: [],
   });

   await rateLimitService.updateRateLimit("review");

   return { success: true, id: docRef.id };
  } catch (error) {
   console.error("Error creating review:", error);
   throw new Error(`Failed to create review: ${error.message}`);
  }
 },

 async updateReview(reviewId, editData, currentStatus) {
  try {
   const database = await databasePromise;
   const ref = doc(database, "reviews", reviewId);

   const sanitizedEditData = {
    ...(editData.dealTitle != null && {
     dealTitle: sanitizeText(editData.dealTitle, 100),
    }),
    description: sanitizeText(editData.description || "", 500),
    rating: editData.rating,
   };

   if (currentStatus === "pendingCreate") {
    await updateDoc(ref, { ...sanitizedEditData });
   } else {
    await updateDoc(ref, {
     editDraft: {
      description: sanitizedEditData.description,
      rating: sanitizedEditData.rating,
      editedDate: new Date().toISOString(),
     },
     status: "pendingEdit",
    });
   }

   return { success: true };
  } catch (error) {
   console.error("Error updating review:", error);
   throw new Error(`Failed to update review: ${error.message}`);
  }
 },

 async deleteReview(review) {
  try {
   const database = await databasePromise;
   const ref = doc(database, "reviews", review.id);

   if (review.status === "pendingCreate") {
    await deleteDoc(ref);
   } else {
    const now = new Date().toISOString();

    await addDoc(collection(database, "reviews", review.id, "statusHistory"), {
     status: "deleted",
     previousStatus: review.status,
     actionedAt: now,
    });

    await updateDoc(ref, {
     status: "deleted",
     deletedDate: now,
     latestRejection: null,
    });

    const COUNTED_STATUSES = ["approved", "pendingEdit", "pendingReport", "reported"];
    if (COUNTED_STATUSES.includes(review.status)) {
     await decrementAggregates(review, database);
    }
   }

   return { success: true };
  } catch (error) {
   console.error("Error deleting review:", error);
   throw new Error(`Failed to delete review: ${error.message}`);
  }
 },

 async deleteReviewDraft(reviewId) {
  try {
   const database = await databasePromise;
   await updateDoc(doc(database, "reviews", reviewId), {
    status: "approved",
    editDraft: null,
   });
   return { success: true };
  } catch (error) {
   console.error("Error deleting review draft:", error);
   throw new Error(`Failed to delete review draft: ${error.message}`);
  }
 },

 async addLike(reviewId, userId, isDisliked = false) {
  try {
   await getValidFirebaseToken();
   const database = await databasePromise;
   const ref = doc(database, "reviews", reviewId);
   if (isDisliked) await updateDoc(ref, { dislikedBy: arrayRemove(userId) });
   await updateDoc(ref, { likedBy: arrayUnion(userId) });
   return { success: true };
  } catch (error) {
   throw new Error(`Failed to like review: ${error.message}`);
  }
 },

 async removeLike(reviewId, userId) {
  try {
   await getValidFirebaseToken();
   const database = await databasePromise;
   await updateDoc(doc(database, "reviews", reviewId), { likedBy: arrayRemove(userId) });
   return { success: true };
  } catch (error) {
   console.error("Error unliking review:", error);
   throw new Error(`Failed to unlike review: ${error.message}`);
  }
 },

 async addDislike(reviewId, userId, isLiked = false) {
  try {
   await getValidFirebaseToken();
   const database = await databasePromise;
   const ref = doc(database, "reviews", reviewId);
   if (isLiked) await updateDoc(ref, { likedBy: arrayRemove(userId) });
   await updateDoc(ref, { dislikedBy: arrayUnion(userId) });
   return { success: true };
  } catch (error) {
   console.error("Error disliking review:", error);
   throw new Error(`Failed to dislike review: ${error.message}`);
  }
 },

 async removeDislike(reviewId, userId) {
  try {
   await getValidFirebaseToken();
   const database = await databasePromise;
   await updateDoc(doc(database, "reviews", reviewId), { dislikedBy: arrayRemove(userId) });
   return { success: true };
  } catch (error) {
   console.error("Error un-disliking review:", error);
   throw new Error(`Failed to un-dislike review: ${error.message}`);
  }
 },

 async likeReview(reviewId, userId) { return this.addLike(reviewId, userId); },
 async dislikeReview(reviewId, userId) { return this.addDislike(reviewId, userId); },

 async reportReview(reviewId, userId, reportData) {
  try {
   const database = await databasePromise;
   const ref = doc(database, "reviews", reviewId);
   const deviceId = getDeviceId();

   const sanitizedReportData = {
    ...reportData,
    reason: reportData.reason,
    description: reportData.description ? sanitizeText(reportData.description, 100) : "",
   };

   await updateDoc(ref, {
    reportedBy: arrayUnion({
     ...sanitizedReportData,
     deviceId,
     id: uuidv4(),
     reportedAt: new Date().toISOString(),
     status: "pendingReport",
     userId,
    }),
    status: "pendingReport",
   });

   return { success: true };
  } catch (error) {
   console.error("Error reporting review:", error);
   throw new Error(`Failed to report review: ${error.message}`);
  }
 },

 async cancelDeleteReview(review) {
  try {
   const database = await databasePromise;
   const hasPendingReports = review.reportedBy?.some((r) => r.status === "pendingReport");
   const newStatus = hasPendingReports ? "pendingReport" : "approved";
   await updateDoc(doc(database, "reviews", review.id), { status: newStatus, deletedDate: null });
   return { success: true };
  } catch (error) {
   console.error("Error cancelling delete review:", error);
   throw new Error(`Failed to cancel delete review: ${error.message}`);
  }
 },

 async updateReviewStatus(reviewId, newStatus, moderatedBy = null, reason = null) {
  try {
   const database = await databasePromise;
   const ref = doc(database, "reviews", reviewId);
   const reviewDoc = await getDoc(ref);

   if (!reviewDoc.exists()) throw new Error("Review not found");

   const currentData = reviewDoc.data();
   const now = new Date();
   const sanitizedReason = reason ? sanitizeText(reason, 500) : null;

   const statusHistoryEntry = {
    status: newStatus,
    previousStatus: currentData.status,
    actionedAt: now,
    moderatedAt: now,
    moderatedBy,
    reason: sanitizedReason,
   };

   await addDoc(collection(database, "reviews", reviewId, "statusHistory"), statusHistoryEntry);

   const updateData = {
    status: newStatus,
    latestRejection: newStatus === "rejected"
     ? { reason: sanitizedReason, moderatedAt: now, moderatedBy }
     : null,
   };

   if (newStatus === "approved" && currentData.editDraft) {
    updateData.description = currentData.editDraft.description;
    updateData.rating = currentData.editDraft.rating;
    updateData.editDraft = null;
    updateData.updatedDate = serverTimestamp();
   }

   await updateDoc(ref, updateData);
   return true;
  } catch (error) {
   console.error("Error updating review status:", error);
   throw new Error(`Failed to update review status: ${error.message}`);
  }
 },
};
